import {
  ArticleOutlined,
  CameraAltOutlined,
  FiberManualRecord,
  ImageOutlined,
  StopCircle,
} from "@mui/icons-material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Box,
  IconButton,
  InputBase,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import type { EmojiClickData } from "emoji-picker-react";
import React, { Suspense, useRef, useState } from "react";

const EmojiPicker = React.lazy(() => import("emoji-picker-react"));

const resolvePreferredAudioMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }
  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/ogg; codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm; codecs=opus",
    "audio/webm",
  ];
  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || "";
};

interface MessageBarProps {
  onSend: (text: string) => Promise<void> | void;
  onAttach: (file: File) => Promise<void> | void;
  onVoice: (blob: Blob) => Promise<void> | void;
  onEmoji: (emoji: string) => void;
  onTyping?: (text: string) => void;
  onTemplateClick?: () => void;
  replyPreview?: {
    author: string;
    preview: string;
  } | null;
  onCancelReply?: () => void;
}

export const MessageBar: React.FC<MessageBarProps> = ({
  onSend,
  onAttach,
  onVoice,
  onEmoji,
  onTyping,
  onTemplateClick,
  replyPreview,
  onCancelReply,
}) => {
  const [text, setText] = useState("");
  const [filePreviews, setFilePreviews] = useState<File[]>([]);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [voicePreview, setVoicePreview] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [attachAnchor, setAttachAnchor] = useState<HTMLElement | null>(null);
  const [elapsed, setElapsed] = useState("00:00");

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const elapsedMsRef = useRef(0);
  const cancelRecordingRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasContent = Boolean(text.trim() || filePreviews.length || voicePreview);

  const formatTime = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const min = String(Math.floor(totalSec / 60)).padStart(2, "0");
    const sec = String(totalSec % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = (stream?: MediaStream | null) => {
    stream?.getTracks().forEach((track) => track.stop());
  };

  const focusInputAtEnd = (
    el: HTMLInputElement | HTMLTextAreaElement | null
  ) => {
    if (!el) return;
    const length = el.value.length;
    el.focus();
    el.setSelectionRange(length, length);
  };

  const normalizeComposerText = (value: string) => {
    const firstTextIndex = value.search(/\S/);
    if (firstTextIndex === -1) return value;
    return (
      value.slice(0, firstTextIndex) +
      value.charAt(firstTextIndex).toUpperCase() +
      value.slice(firstTextIndex + 1)
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      setFilePreviews((current) => [...current, ...files]);
    }
    event.target.value = "";
    setAttachAnchor(null);
  };

  const handleSend = async () => {
    if (filePreviews.length) {
      const files = [...filePreviews];
      setFilePreviews([]);
      for (const file of files) {
        await onAttach(file);
      }
      return;
    }
    if (voicePreview) {
      const nextVoice = voicePreview;
      setVoicePreview(null);
      setAudioUrl(null);
      await onVoice(nextVoice);
      return;
    }
    if (text.trim()) {
      const nextText = text.trim();
      setText("");
      await onSend(nextText);
    }
  };

  const startElapsedTimer = () => {
    clearTimer();
    timerRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      const ms = elapsedMsRef.current + (Date.now() - startTimeRef.current);
      setElapsed(formatTime(ms));
    }, 350);
  };

  const handleStartRecording = async () => {
    try {
      const userStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const preferredMimeType = resolvePreferredAudioMimeType();
      const recorder = preferredMimeType
        ? new MediaRecorder(userStream, { mimeType: preferredMimeType })
        : new MediaRecorder(userStream);
      const chunks: BlobPart[] = [];
      cancelRecordingRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = () => {
        stopStream(userStream);
        clearTimer();
        setElapsed("00:00");
        elapsedMsRef.current = 0;
        startTimeRef.current = null;
        if (cancelRecordingRef.current) {
          cancelRecordingRef.current = false;
          return;
        }
        const firstBlobChunk = chunks.find(
          (chunk): chunk is Blob => chunk instanceof Blob
        );
        const blob = new Blob(chunks, {
          type:
            recorder.mimeType ||
            firstBlobChunk?.type ||
            "audio/webm",
        });
        setVoicePreview(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      streamRef.current = userStream;
      startTimeRef.current = Date.now();
      startElapsedTimer();
      setRecording(true);
      setPaused(false);
    } catch (error) {
      console.error("Error iniciando grabacion:", error);
    }
  };

  const handleStopRecording = () => {
    cancelRecordingRef.current = false;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    setPaused(false);
    clearTimer();
  };

  const handleCancelRecording = () => {
    cancelRecordingRef.current = true;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    setVoicePreview(null);
    setAudioUrl(null);
    setRecording(false);
    setPaused(false);
    clearTimer();
    stopStream(streamRef.current);
  };

  const handlePauseRecording = () => {
    if (!mediaRecorderRef.current) return;
    if (mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      if (startTimeRef.current) {
        elapsedMsRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
      clearTimer();
      setPaused(true);
      return;
    }
    if (mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      startTimeRef.current = Date.now();
      startElapsedTimer();
      setPaused(false);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    onEmoji(emoji);
    setText((current) => normalizeComposerText(current + emoji));
    setTimeout(() => focusInputAtEnd(inputRef.current), 50);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      void audioRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const nextProgress =
      (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setProgress(nextProgress || 0);
    setCurrentTime(formatTime(audioRef.current.currentTime * 1000));
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={0.45}
      p={0.45}
      bgcolor="background.paper"
      borderRadius={2.5}
      border="1px solid"
      borderColor="divider"
      boxShadow="0 8px 24px rgba(15, 23, 42, 0.08)"
    >
      {replyPreview && (
        <Box
          display="flex"
          alignItems="flex-start"
          gap={1}
          sx={{
            px: 0.85,
            py: 0.7,
            borderRadius: 2,
            bgcolor: "rgba(138, 0, 204, 0.08)",
            border: "1px solid rgba(138, 0, 204, 0.14)",
          }}
        >
          <Box
            sx={{
              width: 4,
              alignSelf: "stretch",
              borderRadius: 999,
              bgcolor: "#8a00cc",
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 10.2, fontWeight: 900, color: "#8a00cc" }}>
              {replyPreview.author}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.6,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {replyPreview.preview}
            </Typography>
          </Box>
          {onCancelReply && (
            <IconButton size="small" onClick={onCancelReply}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {filePreviews.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.6}>
          {filePreviews.map((file, index) => {
            const isImage = file.type.startsWith("image/");
            return (
              <Box
                key={`${file.name}-${index}`}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  px: 0.85,
                  py: 0.65,
                  borderRadius: 2,
                  bgcolor: "rgba(138, 0, 204, 0.07)",
                  border: "1px solid rgba(138, 0, 204, 0.12)",
                }}
              >
                {isImage ? (
                  <Avatar
                    variant="rounded"
                    src={URL.createObjectURL(file)}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "rgba(138, 0, 204, 0.12)",
                      color: "#8a00cc",
                    }}
                  >
                    <ArticleOutlined fontSize="small" />
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: 11, fontWeight: 800 }}>
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 9.8, color: "text.secondary" }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    setFilePreviews((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index)
                    )
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {voicePreview && audioUrl && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            px: 0.7,
            py: 0.65,
            borderRadius: 999,
            bgcolor: "rgba(138, 0, 204, 0.08)",
            border: "1px solid rgba(138, 0, 204, 0.14)",
          }}
        >
          <IconButton size="small" onClick={togglePlay} sx={{ bgcolor: "background.paper" }}>
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Typography sx={{ fontSize: 10.6, fontWeight: 800 }}>
            {currentTime}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ borderRadius: 999, height: 4, flex: 1 }}
          />
          <IconButton size="small" onClick={() => setVoicePreview(null)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          <audio
            ref={audioRef}
            src={audioUrl}
            hidden
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setPlaying(false)}
          />
        </Box>
      )}

      {recording && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            px: 0.7,
            py: 0.65,
            borderRadius: 999,
            bgcolor: paused
              ? "rgba(251, 146, 60, 0.16)"
              : "rgba(239, 68, 68, 0.13)",
            border: "1px solid",
            borderColor: paused
              ? "rgba(251, 146, 60, 0.28)"
              : "rgba(239, 68, 68, 0.24)",
          }}
        >
          <MicIcon sx={{ color: paused ? "#c2410c" : "#dc2626" }} />
          <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 900 }}>
            {paused ? "Pausado" : "Grabando"} {elapsed}
          </Typography>
          <IconButton size="small" onClick={handlePauseRecording} sx={{ bgcolor: "background.paper" }}>
            {paused ? (
              <FiberManualRecord fontSize="small" />
            ) : (
              <PauseIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleStopRecording}
            sx={{ bgcolor: "background.paper", color: "#8a00cc" }}
          >
            <StopCircle fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancelRecording}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        bgcolor="background.paper"
        borderRadius={2}
        px={0.65}
        py={0.25}
        gap={0.3}
        sx={{
          "& .MuiIconButton-root": {
            width: 32,
            height: 32,
          },
          "& .MuiSvgIcon-root": {
            fontSize: 18,
          },
        }}
      >
        <Tooltip title="Adjuntar">
          <IconButton
            size="small"
            onClick={(event) => setAttachAnchor(event.currentTarget)}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
        {onTemplateClick && (
          <Tooltip title="Plantillas de WhatsApp">
            <IconButton size="small" onClick={onTemplateClick}>
              <DescriptionOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <Menu
          anchorEl={attachAnchor}
          open={Boolean(attachAnchor)}
          onClose={() => setAttachAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MenuItem onClick={() => imageInputRef.current?.click()}>
            <ListItemIcon>
              <ImageOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Imagenes" secondary="Una o varias imagenes" />
          </MenuItem>
          <MenuItem onClick={() => documentInputRef.current?.click()}>
            <ListItemIcon>
              <ArticleOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Documentos" secondary="PDF, docs y archivos" />
          </MenuItem>
          <MenuItem onClick={() => cameraInputRef.current?.click()}>
            <ListItemIcon>
              <CameraAltOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Camara" secondary="Tomar foto" />
          </MenuItem>
          {onTemplateClick && (
            <MenuItem
              onClick={() => {
                setAttachAnchor(null);
                onTemplateClick();
              }}
            >
              <ListItemIcon>
                <DescriptionOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Plantillas" secondary="WhatsApp aprobadas" />
            </MenuItem>
          )}
        </Menu>
        <input
          type="file"
          hidden
          ref={imageInputRef}
          onChange={handleFileChange}
          accept="image/*"
          multiple
        />
        <input
          type="file"
          hidden
          ref={documentInputRef}
          onChange={handleFileChange}
          multiple
        />
        <input
          type="file"
          hidden
          ref={cameraInputRef}
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
        />

        <InputBase
          inputRef={inputRef}
          placeholder="Escribe un mensaje..."
          fullWidth
          multiline
          maxRows={3}
          value={text}
          onChange={(event) => {
            const nextText = normalizeComposerText(event.target.value);
            setText(nextText);
            onTyping?.(nextText);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.ctrlKey) {
              event.preventDefault();
              void handleSend();
            }
            if (event.key === "Enter" && event.ctrlKey) {
              setText((current) => `${current}\n`);
            }
          }}
          inputProps={{ autoCapitalize: "sentences" }}
          sx={{
            px: 0.45,
            fontSize: 12.75,
          }}
        />

        <Tooltip title="Emoji">
          <IconButton
            size="small"
            onClick={(event) => setEmojiAnchor(event.currentTarget)}
          >
            <InsertEmoticonIcon />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => {
            setEmojiAnchor(null);
            setTimeout(() => focusInputAtEnd(inputRef.current), 50);
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Suspense
            fallback={
              <Box p={2} minWidth={280}>
                <Typography variant="body2">Cargando emojis...</Typography>
              </Box>
            }
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              lazyLoadEmojis
              autoFocusSearch={false}
            />
          </Suspense>
        </Popover>

        <Tooltip title={hasContent ? "Enviar" : "Nota de voz"}>
          <span>
            <IconButton
              size="small"
              onClick={hasContent ? handleSend : handleStartRecording}
              sx={{
                bgcolor: hasContent ? "#8a00cc" : "rgba(138,0,204,0.08)",
                color: hasContent ? "#ffffff" : "#8a00cc",
                width: 32,
                height: 32,
                "&:hover": {
                  bgcolor: hasContent ? "#7b1fa2" : "rgba(138,0,204,0.14)",
                },
              }}
            >
              {hasContent ? <SendIcon /> : <MicIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
